// shabba-dashboard/src/components/analytics/EnhancedPriceChart.tsx
// Enhanced price chart with VWAP/TWAP overlays for advanced technical analysis

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine, Brush } from 'recharts';
import { Eye, EyeOff, BarChart3, Activity, ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMarket } from '../../context/MarketContext';
import { fetchSimpleTWAP, fetchSimpleVWAP } from '../../api/series';

interface ChartDataPoint {
  time: string;
  timeValue: number; // Numeric timestamp for X-axis (milliseconds)
  price: number;
  volume: number;
  vwap?: number;
  vwamp?: number; // Volume Weighted Average Microprice
  twap1m?: number;
  twap5m?: number;
  twap15m?: number;
  twap30m?: number;
  twap1h?: number;
  twap4h?: number;
  timestamp: string;
}

interface EnhancedPriceChartProps {
  height?: number;
  showVolume?: boolean;
  timeRange?: number; // Hours to display
}

export default function EnhancedPriceChart({
  height = 400,
  showVolume = false,
  timeRange = 4 // 4 hours default
}: EnhancedPriceChartProps) {
  const {
    selectedSymbol,
    currentPrice,
    labelForSecId,
    twapDataBySymbol,
    vwapDataBySymbol,
    lastPriceDataBySymbol,
    micropriceDataBySymbol,
    dataVersions,
    stats,
    recentTrades
  } = useMarket();

  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [vwapData, setVwapData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestTimestampRef = useRef<string | null>(null);
  const lastProcessedTwapLengthRef = useRef<number>(0);
  const lastProcessedVwapLengthRef = useRef<number>(0);
  const dataRef = useRef<ChartDataPoint[]>([]);
  const vwapDataRef = useRef<any[]>([]);
  const [visibleTimeframes, setVisibleTimeframes] = useState({
    vwap: true,
    vwamp: true, // Volume Weighted Average Microprice
    twap1m: false,
    twap5m: true,
    twap15m: true,
    twap30m: false,
    twap1h: true,
    twap4h: false,
    lastPrice: true
  });
  
  // Imbalance data state
  const [imbalanceData, setImbalanceData] = useState<Array<{ time: string; timeValue: number; imbalance: number; timestamp: string }>>([]);
  
  // Synchronized tooltip state - shared between both charts
  const [activeTimestamp, setActiveTimestamp] = useState<number | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  
  // Zoom and pan state
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const loadInitialData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        
        // console.log('Loading initial TWAP and VWAP data for symbol:', selectedSymbol);
        
        // First, try to use WebSocket data
        const wsTwapData = twapDataBySymbol.get(selectedSymbol.toUpperCase()) || [];
        const wsVwapData = vwapDataBySymbol.get(selectedSymbol.toUpperCase()) || [];
        const wsLastPriceData = lastPriceDataBySymbol.get(selectedSymbol.toUpperCase()) || [];
        const wsVwampData = micropriceDataBySymbol.get(selectedSymbol.toUpperCase()) || [];
        
        let chartData: ChartDataPoint[] = [];
        let vwapChartData: any[] = [];
        
        // If we have WebSocket data, use it
        if (wsTwapData.length > 0 || wsVwapData.length > 0 || wsLastPriceData.length > 0 || wsVwampData.length > 0) {
          // console.log('Using WebSocket data:', wsTwapData.length, 'TWAP points,', wsVwapData.length, 'VWAP points,', wsLastPriceData.length, 'last price points,', wsVwampData.length, 'VWAMP points');
          
          // Process WebSocket TWAP data
          if (wsTwapData.length > 0) {
            chartData = wsTwapData.map((point: any) => {
              const timestamp = new Date(point.timestamp).getTime();
              return {
                time: new Date(point.timestamp).toLocaleTimeString(),
                timeValue: timestamp, // Numeric timestamp for X-axis
                price: point.lastPrice || 0, // Will be filled from lastPriceData if available
                volume: 0,
                twap1m: point.twap1m,
                twap5m: point.twap5m,
                twap15m: point.twap15m,
                twap30m: point.twap30m,
                twap1h: point.twap1h,
                twap4h: point.twap4h,
                timestamp: point.timestamp
              };
            });
            
            // Sort chart data by timestamp in ascending order
            chartData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          }
          
          // Process WebSocket VWAP data
          if (wsVwapData.length > 0) {
            vwapChartData = wsVwapData.map((point: any) => ({
              time: new Date(point.timestamp).toLocaleTimeString(),
              vwap: point.vwap,
              timestamp: point.timestamp
            }));
          }
          
          // Create maps for efficient lookup
          const vwapMap = new Map<string, number>();
          vwapChartData.forEach(vwap => {
            vwapMap.set(vwap.timestamp, vwap.vwap);
          });
          
          const lastPriceMap = new Map<string, number>();
          wsLastPriceData.forEach(lp => {
            lastPriceMap.set(lp.timestamp, lp.lastPrice);
          });
          
          const vwampMap = new Map<string, number>();
          wsVwampData.forEach(vwamp => {
            vwampMap.set(vwamp.timestamp, vwamp.microprice);
          });
          
          // Sort data by timestamp for interpolation
          const sortedVwapData = vwapChartData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          const sortedLastPriceData = wsLastPriceData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          const sortedVwampData = wsVwampData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          
          chartData.forEach((point) => {
            // Merge VWAP data
            const vwapValue = vwapMap.get(point.timestamp);
            if (vwapValue !== undefined) {
              point.vwap = vwapValue;
            } else {
              // Interpolate VWAP value if missing
              const pointTime = new Date(point.timestamp).getTime();
              
              // Find the closest VWAP data points before and after this time
              let beforeVwap = null;
              let afterVwap = null;
              
              for (let i = 0; i < sortedVwapData.length; i++) {
                const vwapTime = new Date(sortedVwapData[i].timestamp).getTime();
                if (vwapTime <= pointTime) {
                  beforeVwap = sortedVwapData[i];
                } else if (vwapTime > pointTime && !afterVwap) {
                  afterVwap = sortedVwapData[i];
                  break;
                }
              }
              
              // Interpolate between before and after VWAP values
              if (beforeVwap && afterVwap) {
                const beforeTime = new Date(beforeVwap.timestamp).getTime();
                const afterTime = new Date(afterVwap.timestamp).getTime();
                const timeDiff = afterTime - beforeTime;
                const pointDiff = pointTime - beforeTime;
                const ratio = pointDiff / timeDiff;
                
                point.vwap = beforeVwap.vwap + (afterVwap.vwap - beforeVwap.vwap) * ratio;
              } else if (beforeVwap) {
                point.vwap = beforeVwap.vwap;
              } else if (afterVwap) {
                point.vwap = afterVwap.vwap;
              } else if (stats?.vwap) {
                // Fallback to stats VWAP if available
                point.vwap = stats.vwap;
              }
            }
            
            // Merge last price data (for the price line)
            const lastPriceValue = lastPriceMap.get(point.timestamp);
            if (lastPriceValue !== undefined) {
              point.price = lastPriceValue;
            } else if (point.price === 0) {
              // Interpolate last price if missing and not set from TWAP
              const pointTime = new Date(point.timestamp).getTime();
              
              // Find the closest last price data points before and after this time
              let beforeLastPrice = null;
              let afterLastPrice = null;
              
              for (let i = 0; i < sortedLastPriceData.length; i++) {
                const lpTime = new Date(sortedLastPriceData[i].timestamp).getTime();
                if (lpTime <= pointTime) {
                  beforeLastPrice = sortedLastPriceData[i];
                } else if (lpTime > pointTime && !afterLastPrice) {
                  afterLastPrice = sortedLastPriceData[i];
                  break;
                }
              }
              
              // Interpolate between before and after last price values
              if (beforeLastPrice && afterLastPrice) {
                const beforeTime = new Date(beforeLastPrice.timestamp).getTime();
                const afterTime = new Date(afterLastPrice.timestamp).getTime();
                const timeDiff = afterTime - beforeTime;
                const pointDiff = pointTime - beforeTime;
                const ratio = pointDiff / timeDiff;
                
                point.price = beforeLastPrice.lastPrice + (afterLastPrice.lastPrice - beforeLastPrice.lastPrice) * ratio;
              } else if (beforeLastPrice) {
                point.price = beforeLastPrice.lastPrice;
              } else if (afterLastPrice) {
                point.price = afterLastPrice.lastPrice;
              } else {
                // Final fallback to current price only if no historical data available
                point.price = stats?.last || currentPrice || 0;
              }
            }
            
            // Merge VWAMP data (Volume Weighted Average Microprice)
            const vwampValue = vwampMap.get(point.timestamp);
            if (vwampValue !== undefined) {
              point.vwamp = vwampValue;
            } else {
              // Interpolate VWAMP value if missing
              const pointTime = new Date(point.timestamp).getTime();
              
              // Find the closest VWAMP data points before and after this time
              let beforeVwamp = null;
              let afterVwamp = null;
              
              for (let i = 0; i < sortedVwampData.length; i++) {
                const vwampTime = new Date(sortedVwampData[i].timestamp).getTime();
                if (vwampTime <= pointTime) {
                  beforeVwamp = sortedVwampData[i];
                } else if (vwampTime > pointTime && !afterVwamp) {
                  afterVwamp = sortedVwampData[i];
                  break;
                }
              }
              
              // Interpolate between before and after VWAMP values
              if (beforeVwamp && afterVwamp) {
                const beforeTime = new Date(beforeVwamp.timestamp).getTime();
                const afterTime = new Date(afterVwamp.timestamp).getTime();
                const timeDiff = afterTime - beforeTime;
                const pointDiff = pointTime - beforeTime;
                const ratio = pointDiff / timeDiff;
                
                point.vwamp = beforeVwamp.microprice + (afterVwamp.microprice - beforeVwamp.microprice) * ratio;
              } else if (beforeVwamp) {
                point.vwamp = beforeVwamp.microprice;
              } else if (afterVwamp) {
                point.vwamp = afterVwamp.microprice;
              }
            }
          });
          
        // Update refs
        dataRef.current = chartData;
        vwapDataRef.current = vwapChartData;
        lastProcessedTwapLengthRef.current = wsTwapData.length;
        lastProcessedVwapLengthRef.current = wsVwapData.length;
        
        setData(chartData);
        setVwapData(vwapChartData);
        
        // Set the latest timestamp ref
        if (chartData.length > 0) {
          latestTimestampRef.current = chartData[chartData.length - 1].timestamp;
        }
        
        // console.log('Initial chart data prepared from WebSocket:', chartData.length, 'points');
        setLoading(false);
        return;
        }
        
        // Fallback to API if no WebSocket data available
        // console.log('No WebSocket data available, falling back to API');
        const [twapResult, vwapResult] = await Promise.all([
          fetchSimpleTWAP(selectedSymbol, 100).catch(err => {
            // console.warn('TWAP API fetch failed:', err);
            return [];
          }),
          fetchSimpleVWAP(selectedSymbol, 100).catch(err => {
            // console.warn('VWAP API fetch failed:', err);
            return [];
          })
        ]);
        
        // console.log('Initial TWAP data received:', twapResult.length, 'points');
        // console.log('Initial VWAP data received:', vwapResult.length, 'points');
        
        // Process TWAP data
        if (twapResult && twapResult.length > 0) {
          chartData = twapResult.map((point: any) => {
            const timestamp = new Date(point.ts).getTime();
            return {
              time: new Date(point.ts).toLocaleTimeString(),
              timeValue: timestamp, // Numeric timestamp for X-axis
              price: Number(point.last_price),
              volume: 0,
              twap1m: Number(point.twap_1m),
              twap5m: Number(point.twap_5m),
              twap15m: Number(point.twap_15m),
              twap30m: Number(point.twap_30m),
              twap1h: Number(point.twap_1h),
              twap4h: Number(point.twap_4h),
              timestamp: point.ts
            };
          });
          
          chartData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        }
        
        // Process VWAP data
        if (vwapResult && vwapResult.length > 0) {
          vwapChartData = vwapResult.map((point: any) => ({
            time: new Date(point.ts).toLocaleTimeString(),
            vwap: Number(point.vwap),
            timestamp: point.ts
          }));
        }
        
        // Merge VWAP data with TWAP data and interpolate missing VWAP values
        const vwapMap = new Map<string, number>();
        vwapChartData.forEach(vwap => {
          vwapMap.set(vwap.timestamp, vwap.vwap);
        });
        
        const sortedVwapData = vwapChartData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        
        chartData.forEach((point) => {
          const vwapValue = vwapMap.get(point.timestamp);
          if (vwapValue !== undefined) {
            point.vwap = vwapValue;
          } else {
            const pointTime = new Date(point.timestamp).getTime();
            let beforeVwap = null;
            let afterVwap = null;
            
            for (let i = 0; i < sortedVwapData.length; i++) {
              const vwapTime = new Date(sortedVwapData[i].timestamp).getTime();
              if (vwapTime <= pointTime) {
                beforeVwap = sortedVwapData[i];
              } else if (vwapTime > pointTime && !afterVwap) {
                afterVwap = sortedVwapData[i];
                break;
              }
            }
            
            if (beforeVwap && afterVwap) {
              const beforeTime = new Date(beforeVwap.timestamp).getTime();
              const afterTime = new Date(afterVwap.timestamp).getTime();
              const timeDiff = afterTime - beforeTime;
              const pointDiff = pointTime - beforeTime;
              const ratio = pointDiff / timeDiff;
              
              point.vwap = beforeVwap.vwap + (afterVwap.vwap - beforeVwap.vwap) * ratio;
            } else if (beforeVwap) {
              point.vwap = beforeVwap.vwap;
            } else if (afterVwap) {
              point.vwap = afterVwap.vwap;
            } else if (stats?.vwap) {
              point.vwap = stats.vwap;
            }
          }
        });
        
        // Update refs
        dataRef.current = chartData;
        vwapDataRef.current = vwapChartData;
        lastProcessedTwapLengthRef.current = wsTwapData.length;
        lastProcessedVwapLengthRef.current = wsVwapData.length;
        
        setData(chartData);
        setVwapData(vwapChartData);
        
        if (chartData.length > 0) {
          latestTimestampRef.current = chartData[chartData.length - 1].timestamp;
        }
        
        // console.log('Initial chart data prepared from API:', chartData.length, 'points');
      } catch (err) {
        // console.error('Error loading initial data:', err);
        // Don't set error if we have WebSocket data as fallback
        if (twapDataBySymbol.get(selectedSymbol.toUpperCase())?.length === 0 && 
            vwapDataBySymbol.get(selectedSymbol.toUpperCase())?.length === 0) {
          setError(err instanceof Error ? err.message : 'Failed to load data');
        }
      } finally {
        setLoading(false);
      }
    }, [selectedSymbol, twapDataBySymbol, vwapDataBySymbol, lastPriceDataBySymbol, micropriceDataBySymbol, currentPrice, stats]);

  const loadNewData = useCallback(async () => {
      try {
        // Check for new WebSocket data
        const wsTwapData = twapDataBySymbol.get(selectedSymbol.toUpperCase()) || [];
        const wsVwapData = vwapDataBySymbol.get(selectedSymbol.toUpperCase()) || [];
        const wsLastPriceData = lastPriceDataBySymbol.get(selectedSymbol.toUpperCase()) || [];
        const wsVwampData = micropriceDataBySymbol.get(selectedSymbol.toUpperCase()) || [];
        
        // Only process if we have new data (check by length)
        const hasNewTwapData = wsTwapData.length > lastProcessedTwapLengthRef.current;
        const hasNewVwapData = wsVwapData.length > lastProcessedVwapLengthRef.current;
        
        if (!hasNewTwapData && !hasNewVwapData) {
          return; // No new data, skip update completely
        }
        
        const latestTimestamp = latestTimestampRef.current;
        const currentData = dataRef.current;
        const currentVwapData = vwapDataRef.current;
        
        // Double-check: verify we actually have new timestamps to avoid processing duplicates
        if (latestTimestamp) {
          const hasNewTimestamps = (hasNewTwapData && wsTwapData.some((p: any) => p.timestamp > latestTimestamp)) ||
                                  (hasNewVwapData && wsVwapData.some((p: any) => p.timestamp > latestTimestamp));
          
          if (!hasNewTimestamps) {
            // No new timestamps, just update the length refs and return
            lastProcessedTwapLengthRef.current = wsTwapData.length;
            lastProcessedVwapLengthRef.current = wsVwapData.length;
            return;
          }
        }
        
        // Process new WebSocket TWAP data (only the new points)
        let newTwapData: ChartDataPoint[] = [];
        if (hasNewTwapData && wsTwapData.length > 0) {
          const newPoints = wsTwapData.slice(lastProcessedTwapLengthRef.current);
          newTwapData = newPoints
            .filter((point: any) => !latestTimestamp || point.timestamp > latestTimestamp)
            .map((point: any) => {
              const timestamp = new Date(point.timestamp).getTime();
              return {
                time: new Date(point.timestamp).toLocaleTimeString(),
                timeValue: timestamp, // Numeric timestamp for X-axis
                price: point.lastPrice || 0, // Will be filled from lastPriceData if available
                volume: 0,
                twap1m: point.twap1m,
                twap5m: point.twap5m,
                twap15m: point.twap15m,
                twap30m: point.twap30m,
                twap1h: point.twap1h,
                twap4h: point.twap4h,
                timestamp: point.timestamp,
                vwap: undefined as number | undefined
              };
            });
        }
        
        // Process new WebSocket VWAP data (only the new points)
        let newVwapData: any[] = [];
        if (hasNewVwapData && wsVwapData.length > 0) {
          const newPoints = wsVwapData.slice(lastProcessedVwapLengthRef.current);
          newVwapData = newPoints
            .filter((point: any) => !latestTimestamp || point.timestamp > latestTimestamp)
            .map((point: any) => ({
              time: new Date(point.timestamp).toLocaleTimeString(),
              vwap: point.vwap,
              timestamp: point.timestamp
            }));
        }
        
        // Only update if we have actual new data points
        if (newTwapData.length > 0 || newVwapData.length > 0) {
          // console.log('Adding', newTwapData.length, 'new TWAP points,', newVwapData.length, 'new VWAP points from WebSocket');
          
          // Update refs to track processed data
          lastProcessedTwapLengthRef.current = wsTwapData.length;
          lastProcessedVwapLengthRef.current = wsVwapData.length;
          
          // Create maps for efficient lookup
          const vwapMap = new Map<string, number>();
          newVwapData.forEach(vwap => {
            vwapMap.set(vwap.timestamp, vwap.vwap);
          });
          
          const lastPriceMap = new Map<string, number>();
          wsLastPriceData.forEach(lp => {
            lastPriceMap.set(lp.timestamp, lp.lastPrice);
          });
          
          const vwampMap = new Map<string, number>();
          wsVwampData.forEach(vwamp => {
            vwampMap.set(vwamp.timestamp, vwamp.microprice);
          });
          
          // Sort VWAMP data for interpolation
          const sortedVwampData = wsVwampData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          
          // Update VWAP data
          const allVwapData = [...currentVwapData, ...newVwapData].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          // Keep all data for historical chart reconstruction (no limit)
          vwapDataRef.current = allVwapData;
          
          // Interpolate VWAP and last price ONLY for new TWAP points (not existing ones)
          // This prevents recalculating interpolation for points that already have values
          newTwapData.forEach(point => {
            // Merge VWAP data
            const vwapValue = vwapMap.get(point.timestamp);
            if (vwapValue !== undefined) {
              point.vwap = vwapValue;
            } else {
              // Only interpolate if this is a truly new point
              // Use current VWAP data (which includes existing + new) for interpolation
              const pointTime = new Date(point.timestamp).getTime();
              let beforeVwap = null;
              let afterVwap = null;
              
              // Search in all VWAP data (existing + new) for interpolation
              const allVwapDataForInterpolation = [...currentVwapData, ...newVwapData].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
              for (let i = 0; i < allVwapDataForInterpolation.length; i++) {
                const vwapTime = new Date(allVwapDataForInterpolation[i].timestamp).getTime();
                if (vwapTime <= pointTime) {
                  beforeVwap = allVwapDataForInterpolation[i];
                } else if (vwapTime > pointTime && !afterVwap) {
                  afterVwap = allVwapDataForInterpolation[i];
                  break;
                }
              }
              
              if (beforeVwap && afterVwap) {
                const beforeTime = new Date(beforeVwap.timestamp).getTime();
                const afterTime = new Date(afterVwap.timestamp).getTime();
                const timeDiff = afterTime - beforeTime;
                const pointDiff = pointTime - beforeTime;
                const ratio = pointDiff / timeDiff;
                
                point.vwap = beforeVwap.vwap + (afterVwap.vwap - beforeVwap.vwap) * ratio;
              } else if (beforeVwap) {
                point.vwap = beforeVwap.vwap;
              } else if (afterVwap) {
                point.vwap = afterVwap.vwap;
              } else if (stats?.vwap) {
                point.vwap = stats.vwap;
              }
            }
            
            // Merge last price data (for the price line)
            const lastPriceValue = lastPriceMap.get(point.timestamp);
            if (lastPriceValue !== undefined) {
              point.price = lastPriceValue;
            } else if (point.price === 0) {
              // Interpolate last price if missing and not set from TWAP
              const pointTime = new Date(point.timestamp).getTime();
              const sortedLastPriceData = wsLastPriceData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
              
              // Find the closest last price data points before and after this time
              let beforeLastPrice = null;
              let afterLastPrice = null;
              
              for (let i = 0; i < sortedLastPriceData.length; i++) {
                const lpTime = new Date(sortedLastPriceData[i].timestamp).getTime();
                if (lpTime <= pointTime) {
                  beforeLastPrice = sortedLastPriceData[i];
                } else if (lpTime > pointTime && !afterLastPrice) {
                  afterLastPrice = sortedLastPriceData[i];
                  break;
                }
              }
              
              // Interpolate between before and after last price values
              if (beforeLastPrice && afterLastPrice) {
                const beforeTime = new Date(beforeLastPrice.timestamp).getTime();
                const afterTime = new Date(afterLastPrice.timestamp).getTime();
                const timeDiff = afterTime - beforeTime;
                const pointDiff = pointTime - beforeTime;
                const ratio = pointDiff / timeDiff;
                
                point.price = beforeLastPrice.lastPrice + (afterLastPrice.lastPrice - beforeLastPrice.lastPrice) * ratio;
              } else if (beforeLastPrice) {
                point.price = beforeLastPrice.lastPrice;
              } else if (afterLastPrice) {
                point.price = afterLastPrice.lastPrice;
              } else {
                // Final fallback to current price only if no historical data available
                point.price = stats?.last || currentPrice || 0;
              }
            }
            
            // Merge VWAMP data (Volume Weighted Average Microprice)
            const vwampValue = vwampMap.get(point.timestamp);
            if (vwampValue !== undefined) {
              point.vwamp = vwampValue;
            } else {
              // Interpolate VWAMP value if missing
              const pointTime = new Date(point.timestamp).getTime();
              
              // Find the closest VWAMP data points before and after this time
              let beforeVwamp = null;
              let afterVwamp = null;
              
              for (let i = 0; i < sortedVwampData.length; i++) {
                const vwampTime = new Date(sortedVwampData[i].timestamp).getTime();
                if (vwampTime <= pointTime) {
                  beforeVwamp = sortedVwampData[i];
                } else if (vwampTime > pointTime && !afterVwamp) {
                  afterVwamp = sortedVwampData[i];
                  break;
                }
              }
              
              // Interpolate between before and after VWAMP values
              if (beforeVwamp && afterVwamp) {
                const beforeTime = new Date(beforeVwamp.timestamp).getTime();
                const afterTime = new Date(afterVwamp.timestamp).getTime();
                const timeDiff = afterTime - beforeTime;
                const pointDiff = pointTime - beforeTime;
                const ratio = pointDiff / timeDiff;
                
                point.vwamp = beforeVwamp.microprice + (afterVwamp.microprice - beforeVwamp.microprice) * ratio;
              } else if (beforeVwamp) {
                point.vwamp = beforeVwamp.microprice;
              } else if (afterVwamp) {
                point.vwamp = afterVwamp.microprice;
              }
            }
          });
          
          // IMPORTANT: Don't modify existing points' VWAP values
          // Only new points should have their VWAP calculated
          
          // Update chart data - only append new points
          if (newTwapData.length > 0) {
            latestTimestampRef.current = newTwapData[newTwapData.length - 1].timestamp;
            
            // Create new array with existing data + new points
            // Preserve existing points' references when possible to help Recharts optimize
            const combined = [...currentData, ...newTwapData];
            combined.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            // Keep all data for historical chart reconstruction (no limit)
            
            // Only update state if the data actually changed (compare last point)
            const dataChanged = combined.length !== currentData.length || 
                              (combined.length > 0 && currentData.length > 0 &&
                               combined[combined.length - 1].timestamp !== currentData[currentData.length - 1]?.timestamp);
            
            if (dataChanged) {
              dataRef.current = combined;
              // Use functional update to prevent unnecessary re-renders
              setData(prevData => {
                // Double-check: only update if actually different
                if (prevData.length !== combined.length ||
                    (prevData.length > 0 && combined.length > 0 &&
                     prevData[prevData.length - 1].timestamp !== combined[combined.length - 1].timestamp)) {
                  return combined;
                }
                return prevData; // Return same reference to prevent re-render
              });
            }
          } else {
            // Even if no new TWAP data, we might need to update VWAP interpolation for existing points
            // BUT: We should NOT do this as it causes re-renders. VWAP interpolation should only
            // happen when new TWAP points are added, not when new VWAP data arrives.
            // Existing points keep their existing VWAP values (or undefined if they never had one)
          }
          
          // Only update VWAP state if it changed
          const vwapChanged = allVwapData.length !== currentVwapData.length ||
                             (allVwapData.length > 0 && currentVwapData.length > 0 &&
                              allVwapData[allVwapData.length - 1].timestamp !== currentVwapData[currentVwapData.length - 1]?.timestamp);
          
          if (vwapChanged) {
            vwapDataRef.current = allVwapData;
            // Use functional update to prevent unnecessary re-renders
            setVwapData(prevVwapData => {
              // Double-check: only update if actually different
              if (prevVwapData.length !== allVwapData.length ||
                  (prevVwapData.length > 0 && allVwapData.length > 0 &&
                   prevVwapData[prevVwapData.length - 1].timestamp !== allVwapData[allVwapData.length - 1].timestamp)) {
                return allVwapData;
              }
              return prevVwapData; // Return same reference to prevent re-render
            });
          }
          
          return;
        }
        
        // Fallback to API if no new WebSocket data (disabled - data comes from WebSocket only)
        // Data should come from WebSocket, HTTP API is not available
        // No need to fetch from API - wait for WebSocket data
        return;
      } catch (err) {
        // console.error('Error loading new data:', err);
      }
    }, [selectedSymbol, twapDataBySymbol, vwapDataBySymbol, lastPriceDataBySymbol, micropriceDataBySymbol, currentPrice, stats]);

  useEffect(() => {
    // Reset refs when symbol changes
    lastProcessedTwapLengthRef.current = 0;
    lastProcessedVwapLengthRef.current = 0;
    latestTimestampRef.current = null;
    dataRef.current = [];
    vwapDataRef.current = [];
    
    // Load initial data
    loadInitialData();
  }, [selectedSymbol, timeRange, loadInitialData]);
  
  // Track last processed versions to avoid reprocessing
  const lastProcessedTwapVersionRef = useRef<number>(0);
  const lastProcessedVwapVersionRef = useRef<number>(0);
  
  // Reactive update: Watch for version changes (when new WebSocket data arrives)
  // This triggers immediately when new TWAP or VWAP data arrives via WebSocket
  const symbolKey = selectedSymbol.toUpperCase();
  const symbolVersions = dataVersions[symbolKey];
  
  useEffect(() => {
    if (!symbolVersions) return;
    
    const twapVersion = symbolVersions.twap || 0;
    const vwapVersion = symbolVersions.vwap || 0;
    
    // Only process if version actually increased (new data arrived)
    const hasNewTwap = twapVersion > lastProcessedTwapVersionRef.current;
    const hasNewVwap = vwapVersion > lastProcessedVwapVersionRef.current;
    
    if (hasNewTwap || hasNewVwap) {
      lastProcessedTwapVersionRef.current = twapVersion;
      lastProcessedVwapVersionRef.current = vwapVersion;
      loadNewData();
    }
  }, [symbolVersions?.twap, symbolVersions?.vwap, selectedSymbol, loadNewData]);
  
  // Reset version refs when symbol changes
  useEffect(() => {
    lastProcessedTwapVersionRef.current = 0;
    lastProcessedVwapVersionRef.current = 0;
  }, [selectedSymbol]);

  const toggleTimeframe = (timeframe: keyof typeof visibleTimeframes) => {
    setVisibleTimeframes(prev => ({
      ...prev,
      [timeframe]: !prev[timeframe]
    }));
  };

  // Zoom and pan control functions
  // Process imbalance data from micropriceDataBySymbol (from VWAMP messages)
  useEffect(() => {
    const wsVwampData = micropriceDataBySymbol.get(selectedSymbol.toUpperCase()) || [];
    const imbalanceChartData = wsVwampData
      .filter((point: any) => 
        point.imbalance !== undefined && 
        point.imbalance !== null && 
        Number.isFinite(point.imbalance) &&
        point.timestamp
      )
      .map((point: any) => {
        const timestamp = new Date(point.timestamp).getTime();
        return {
          time: new Date(point.timestamp).toLocaleTimeString(),
          timeValue: timestamp,
          imbalance: Number(point.imbalance), // Ensure it's a number
          timestamp: point.timestamp
        };
      })
      .sort((a, b) => a.timeValue - b.timeValue);
    
    setImbalanceData(imbalanceChartData);
  }, [micropriceDataBySymbol, selectedSymbol, dataVersions]);

  const formatPrice = (value: number) => `${value.toFixed(2)}`;
  
  // Format time for X-axis ticks
  const formatTimeTick = (tickItem: number) => {
    const date = new Date(tickItem);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };
  
  // Calculate base X-axis domain based on the union of both datasets
  const baseXAxisDomain = useMemo(() => {
    // Get time ranges from both datasets
    const priceTimes = data.length > 0 ? data.map(d => d.timeValue) : [];
    const imbalanceTimes = imbalanceData.length > 0 ? imbalanceData.map(d => d.timeValue) : [];
    
    // Combine all times to get the full range (union)
    const allTimes = [...priceTimes, ...imbalanceTimes];
    
    if (allTimes.length === 0) return undefined;
    
    // Use the union: the full range covering both datasets
    const minTime = Math.min(...allTimes);
    const maxTime = Math.max(...allTimes);
    
    // Add small padding
    const padding = (maxTime - minTime) * 0.02 || 1000;
    return [minTime - padding, maxTime + padding] as [number, number];
  }, [data, imbalanceData]);

  // Calculate actual X-axis domain (with zoom applied)
  const xAxisDomain = useMemo(() => {
    if (!baseXAxisDomain) return undefined;
    return zoomDomain || baseXAxisDomain;
  }, [baseXAxisDomain, zoomDomain]);

  // Filter data based on zoom domain for both charts to ensure synchronization
  const filteredData = useMemo(() => {
    if (!xAxisDomain) return data;
    const [minTime, maxTime] = xAxisDomain;
    return data.filter(d => d.timeValue >= minTime && d.timeValue <= maxTime);
  }, [data, xAxisDomain]);

  const filteredImbalanceData = useMemo(() => {
    if (!xAxisDomain) return imbalanceData;
    const [minTime, maxTime] = xAxisDomain;
    return imbalanceData.filter(d => d.timeValue >= minTime && d.timeValue <= maxTime);
  }, [imbalanceData, xAxisDomain]);

  // Calculate X-axis ticks for alignment between charts
  const xAxisTicks = useMemo(() => {
    if (!xAxisDomain) return undefined;
    const [minTime, maxTime] = xAxisDomain;
    const tickCount = 6; // Number of ticks to show
    const interval = (maxTime - minTime) / (tickCount - 1);
    const ticks: number[] = [];
    for (let i = 0; i < tickCount; i++) {
      ticks.push(minTime + (interval * i));
    }
    return ticks;
  }, [xAxisDomain]);

  // Synchronized tooltip component for price chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    // Always use activeTimestamp if available (from mouse tracking), otherwise use label from recharts
    const timestampValue = activeTimestamp !== null ? activeTimestamp : (typeof label === 'number' ? label : null);
    // Show tooltip if activeTimestamp is set (mouse is over the chart)
    const shouldShow = activeTimestamp !== null && timestampValue !== null;
    
    if (!shouldShow || !timestampValue) {
      return null;
    }

    // Find the closest PREVIOUS data point to the timestamp (or exact match)
    // This ensures we show the most recent value at or before the synchronized timestamp
    let closestData: ChartDataPoint | null = null;
    
    if (activeTimestamp !== null) {
      // Find the closest previous point (timeValue <= activeTimestamp) or exact match
      const previousPoints = data.filter(d => d.timeValue <= activeTimestamp);
      if (previousPoints.length > 0) {
        // Get the most recent previous point (closest to but not after the timestamp)
        closestData = previousPoints.reduce((prev, curr) => {
          return curr.timeValue > prev.timeValue ? curr : prev;
        });
      }
      // If no previous point exists, closestData remains null (will show N/A for all values)
    } else if (payload?.[0]?.payload) {
      // Fallback to payload if no activeTimestamp
      closestData = payload[0].payload as ChartDataPoint;
    }
    
    // Always show tooltip if we have a timestamp (even if no data), but return null if no timestamp
    if (activeTimestamp === null && !payload?.[0]?.payload) return null;

    // Format timestamp - always use the synchronized timestamp
    const date = new Date(timestampValue);
    const timestamp = date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    // Define the order of fields to display with their colors
    const fieldOrder = [
      { key: 'price', label: 'Last Price', color: '#f59e0b', visibleKey: 'lastPrice' },
      { key: 'vwamp', label: 'Microprice', color: '#ec4899', visibleKey: 'vwamp' },
      { key: 'vwap', label: 'VWAP', color: '#8b5cf6', visibleKey: 'vwap' },
      { key: 'twap1m', label: '1min TWAP', color: '#ef4444', visibleKey: 'twap1m' },
      { key: 'twap5m', label: '5min TWAP', color: '#3b82f6', visibleKey: 'twap5m' },
      { key: 'twap15m', label: '15min TWAP', color: '#10b981', visibleKey: 'twap15m' },
      { key: 'twap30m', label: '30min TWAP', color: '#f97316', visibleKey: 'twap30m' },
      { key: 'twap1h', label: '1hr TWAP', color: '#06b6d4', visibleKey: 'twap1h' },
      { key: 'twap4h', label: '4hr TWAP', color: '#a855f7', visibleKey: 'twap4h' }
    ];

    return (
      <div
        style={{
          backgroundColor: '#1e293b',
          border: '1px solid #475569',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: '11px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <div style={{ color: '#f1f5f9', marginBottom: '10px', fontWeight: '600', borderBottom: '1px solid #475569', paddingBottom: '6px', fontSize: '12px' }}>
          {timestamp}
        </div>
        {fieldOrder.map(({ key, label: fieldLabel, color, visibleKey }) => {
          // Only show if the line is toggled on
          if (!visibleTimeframes[visibleKey as keyof typeof visibleTimeframes]) {
            return null;
          }

          // Get value from closest previous data point, or null if no data exists
          const value = closestData ? (closestData[key as keyof ChartDataPoint] as number | undefined) : null;
          
          // Show the field even if value is null/undefined (display as "N/A" or "0")
          // This ensures all toggled lines appear in the tooltip
          
          return (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '4px',
                color: '#e2e8f0'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0
                  }}
                />
                <span style={{ color: '#94a3b8' }}>{fieldLabel}:</span>
              </div>
              <span style={{ color: color, fontWeight: '600', fontSize: '11px' }}>
                {value !== null && value !== undefined ? formatPrice(value as number) + ' TZS' : 'N/A'}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Synchronized tooltip component for imbalance chart
  const ImbalanceTooltip = ({ active, payload, label }: any) => {
    // Always use activeTimestamp if available (from mouse tracking on top chart), otherwise use label from recharts
    const timestampValue = activeTimestamp !== null ? activeTimestamp : (typeof label === 'number' ? label : null);
    // Show tooltip if activeTimestamp is set (mouse is over either chart)
    const shouldShow = activeTimestamp !== null && timestampValue !== null;
    
    if (!shouldShow || !timestampValue) return null;
    
    // Find the closest PREVIOUS data point to the timestamp (or exact match)
    // This ensures we show the most recent value at or before the synchronized timestamp
    let closestData: any = null;
    
    if (activeTimestamp !== null) {
      // Find the closest previous point (timeValue <= activeTimestamp) or exact match
      const previousPoints = imbalanceData.filter(d => d.timeValue <= activeTimestamp);
      if (previousPoints.length > 0) {
        // Get the most recent previous point (closest to but not after the timestamp)
        closestData = previousPoints.reduce((prev, curr) => {
          return curr.timeValue > prev.timeValue ? curr : prev;
        });
      }
      // If no previous point exists, closestData remains null (will show N/A)
    } else if (payload?.[0]?.payload) {
      // Fallback to payload if no activeTimestamp
      closestData = payload[0].payload;
    }
    
    // Always show tooltip if we have a timestamp (even if no data), but return null if no timestamp
    if (activeTimestamp === null && !payload?.[0]?.payload) return null;

    // Format timestamp in readable format
    const date = new Date(timestampValue);
    const timestamp = date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    return (
      <div
        style={{
          backgroundColor: '#1e293b',
          border: '1px solid #475569',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '10px',
          maxWidth: '200px',
          position: 'relative',
          top: '-30px',
          maxHeight: '60px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
        }}
      >
        <div style={{ color: '#f1f5f9', marginBottom: '6px', fontWeight: '600', borderBottom: '1px solid #475569', paddingBottom: '4px', fontSize: '11px' }}>
          {timestamp}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', color: '#e2e8f0' }}>
          <span style={{ color: '#94a3b8' }}>Imbalance:</span>
          <span style={{ color: '#ec4899', fontWeight: '600' }}>
            {closestData && closestData.imbalance !== null && closestData.imbalance !== undefined 
              ? closestData.imbalance.toFixed(3) 
              : 'N/A'}
          </span>
        </div>
      </div>
    );
  };

  // Zoom and pan handlers
  const handleZoomIn = () => {
    if (!baseXAxisDomain || data.length === 0) return;
    const [minTime, maxTime] = baseXAxisDomain;
    const currentDomain = zoomDomain || baseXAxisDomain;
    const [currentMin, currentMax] = currentDomain;
    
    const center = (currentMin + currentMax) / 2;
    const range = currentMax - currentMin;
    const newRange = range * 0.6; // Zoom in by 40% (more aggressive)
    
    const newMin = center - newRange / 2;
    const newMax = center + newRange / 2;
    
    // Only apply boundaries if we're at the edges
    const finalMin = newMin < minTime ? minTime : newMin;
    const finalMax = newMax > maxTime ? maxTime : newMax;
    
    // Only update if we can actually zoom in more (range should decrease)
    if (finalMax - finalMin < range) {
      setZoomDomain([finalMin, finalMax]);
      setZoomLevel(prev => prev * 1.67);
    }
  };

  const handleZoomOut = () => {
    if (!baseXAxisDomain) return;
    const [minTime, maxTime] = baseXAxisDomain;
    const currentDomain = zoomDomain || baseXAxisDomain;
    const [currentMin, currentMax] = currentDomain;
    
    const center = (currentMin + currentMax) / 2;
    const range = currentMax - currentMin;
    const newRange = range * 1.43; // Zoom out by 43%
    
    const newMin = Math.max(minTime, center - newRange / 2);
    const newMax = Math.min(maxTime, center + newRange / 2);
    
    // If we've zoomed out to the full range, reset zoom
    if (Math.abs(newMin - minTime) < 1000 && Math.abs(newMax - maxTime) < 1000) {
      setZoomDomain(null);
      setZoomLevel(1);
    } else {
      setZoomDomain([newMin, newMax]);
      setZoomLevel(prev => prev / 1.43);
    }
  };

  const handlePanLeft = () => {
    if (!baseXAxisDomain || !zoomDomain) return;
    const [minTime, maxTime] = baseXAxisDomain;
    const [currentMin, currentMax] = zoomDomain;
    const range = currentMax - currentMin;
    const panAmount = range * 0.2;
    
    const newMin = Math.max(minTime, currentMin - panAmount);
    const newMax = newMin + range;
    
    if (newMax <= maxTime) {
      setZoomDomain([newMin, newMax]);
    }
  };

  const handlePanRight = () => {
    if (!baseXAxisDomain || !zoomDomain) return;
    const [minTime, maxTime] = baseXAxisDomain;
    const [currentMin, currentMax] = zoomDomain;
    const range = currentMax - currentMin;
    const panAmount = range * 0.2;
    
    const newMax = Math.min(maxTime, currentMax + panAmount);
    const newMin = newMax - range;
    
    if (newMin >= minTime) {
      setZoomDomain([newMin, newMax]);
    }
  };

  const handleResetZoom = () => {
    setZoomDomain(null);
    setZoomLevel(1);
  };

  const handleBrushChange = (newState: any) => {
    if (newState && newState.startIndex !== undefined && newState.endIndex !== undefined) {
      // Convert brush indices to time domain using filtered data
      const allData = [...filteredData, ...filteredImbalanceData].sort((a, b) => a.timeValue - b.timeValue);
      if (allData.length > 0 && newState.startIndex >= 0 && newState.endIndex < allData.length) {
        const startTime = allData[newState.startIndex]?.timeValue;
        const endTime = allData[newState.endIndex]?.timeValue;
        if (startTime && endTime && startTime < endTime) {
          setZoomDomain([startTime, endTime]);
          const range = baseXAxisDomain ? baseXAxisDomain[1] - baseXAxisDomain[0] : 1;
          const zoomedRange = endTime - startTime;
          setZoomLevel(range / zoomedRange);
        }
      }
    } else {
      setZoomDomain(null);
      setZoomLevel(1);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#1e293b',
        borderRadius: 8,
        border: '1px solid #334155'
      }}>
        <div style={{ color: '#94a3b8' }}>Loading chart data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#1e293b',
        borderRadius: 8,
        border: '1px solid #334155'
      }}>
        <div style={{ color: '#ef4444' }}>Error: {error}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{ 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#1e293b',
        borderRadius: 8,
        border: '1px solid #334155'
      }}>
        <div style={{ color: '#94a3b8' }}>No data available for {selectedSymbol}</div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#1e293b',
      borderRadius: 8,
      padding: 16,
      border: '1px solid #334155'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ 
          margin: 0, 
          color: '#f1f5f9', 
          fontSize: 16,
          fontWeight: 600
        }}>
            Enhanced Price Chart - {labelForSecId(selectedSymbol)}
          </h3>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          {data.length} data points
        </div>
      </div>

      {/* Zoom and Pan Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        padding: 10,
        backgroundColor: '#0f172a',
        borderRadius: 8,
        border: '1px solid #334155',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.2)'
      }}>
        <span style={{ fontSize: 11, color: '#94a3b8', marginRight: 8 }}>Zoom & Pan:</span>
        <button
          onClick={handleZoomIn}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 12px',
            borderRadius: 6,
            backgroundColor: '#1e293b',
            color: '#3b82f6',
            border: '1px solid #334155',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 500,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2563eb';
            e.currentTarget.style.color = '#ffffff';
            e.currentTarget.style.borderColor = '#2563eb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#1e293b';
            e.currentTarget.style.color = '#3b82f6';
            e.currentTarget.style.borderColor = '#334155';
          }}
        >
          <ZoomIn size={12} />
          Zoom In
        </button>
        <button
          onClick={handleZoomOut}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 12px',
            borderRadius: 6,
            backgroundColor: '#1e293b',
            color: '#3b82f6',
            border: '1px solid #334155',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 500,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2563eb';
            e.currentTarget.style.color = '#ffffff';
            e.currentTarget.style.borderColor = '#2563eb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#1e293b';
            e.currentTarget.style.color = '#3b82f6';
            e.currentTarget.style.borderColor = '#334155';
          }}
        >
          <ZoomOut size={12} />
          Zoom Out
        </button>
        {zoomDomain && (
          <>
            <button
              onClick={handlePanLeft}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 12px',
                borderRadius: 6,
                backgroundColor: '#1e293b',
                color: '#f59e0b',
                border: '1px solid #334155',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 500,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#d97706';
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.borderColor = '#d97706';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1e293b';
                e.currentTarget.style.color = '#f59e0b';
                e.currentTarget.style.borderColor = '#334155';
              }}
            >
              <ChevronLeft size={12} />
              Pan Left
            </button>
            <button
              onClick={handlePanRight}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 12px',
                borderRadius: 6,
                backgroundColor: '#1e293b',
                color: '#f59e0b',
                border: '1px solid #334155',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 500,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#d97706';
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.borderColor = '#d97706';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1e293b';
                e.currentTarget.style.color = '#f59e0b';
                e.currentTarget.style.borderColor = '#334155';
              }}
            >
              Pan Right
              <ChevronRight size={12} />
            </button>
          </>
        )}
        <button
          onClick={handleResetZoom}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 12px',
            borderRadius: 6,
            backgroundColor: '#1e293b',
            color: '#10b981',
            border: '1px solid #334155',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 500,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#059669';
            e.currentTarget.style.color = '#ffffff';
            e.currentTarget.style.borderColor = '#059669';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#1e293b';
            e.currentTarget.style.color = '#10b981';
            e.currentTarget.style.borderColor = '#334155';
          }}
        >
          <RotateCcw size={12} />
          Reset
        </button>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>
          {zoomDomain ? `Zoom: ${zoomLevel.toFixed(1)}x` : 'Full Range'}
        </div>
      </div>

      {/* Toggle Controls */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 16,
        padding: 12,
        backgroundColor: '#0f172a',
        borderRadius: 8,
        border: '1px solid #334155',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.2)'
      }}>
        {[
          { key: 'lastPrice', label: 'Last Price', color: '#f59e0b' },
          { key: 'vwap', label: 'VWAP', color: '#8b5cf6' },
          { key: 'vwamp', label: 'Microprice', color: '#ec4899' },
          { key: 'twap1m', label: '1min TWAP', color: '#ef4444' },
          { key: 'twap5m', label: '5min TWAP', color: '#3b82f6' },
          { key: 'twap15m', label: '15min TWAP', color: '#10b981' },
          { key: 'twap30m', label: '30min TWAP', color: '#f97316' },
          { key: 'twap1h', label: '1hr TWAP', color: '#06b6d4' },
          { key: 'twap4h', label: '4hr TWAP', color: '#a855f7' },
        ].map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => toggleTimeframe(key as keyof typeof visibleTimeframes)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '6px 12px',
              borderRadius: 6,
              backgroundColor: visibleTimeframes[key as keyof typeof visibleTimeframes] ? `${color}25` : 'transparent',
              color: visibleTimeframes[key as keyof typeof visibleTimeframes] ? color : '#94a3b8',
              border: `1px solid ${visibleTimeframes[key as keyof typeof visibleTimeframes] ? color : '#475569'}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              fontSize: 11,
              fontWeight: visibleTimeframes[key as keyof typeof visibleTimeframes] ? 600 : 400,
              boxShadow: visibleTimeframes[key as keyof typeof visibleTimeframes] ? `0 2px 4px ${color}20` : 'none'
            }}
            onMouseEnter={(e) => {
              if (!visibleTimeframes[key as keyof typeof visibleTimeframes]) {
                e.currentTarget.style.backgroundColor = '#1e293b';
                e.currentTarget.style.borderColor = color;
              }
            }}
            onMouseLeave={(e) => {
              if (!visibleTimeframes[key as keyof typeof visibleTimeframes]) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#475569';
              }
            }}
          >
            {visibleTimeframes[key as keyof typeof visibleTimeframes] ?
              <Eye size={10} /> : <EyeOff size={10} />
            }
            {label}
          </button>
        ))}
      </div>

      <div 
        style={{ height, position: 'relative' }}
        onMouseMove={(e) => {
          // Convert mouse X position to timestamp value - this makes the yellow line follow the cursor
          if (!xAxisDomain) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const width = rect.width;
          const [minTime, maxTime] = xAxisDomain;
          const timeRange = maxTime - minTime;
          // Account for chart padding (Y-axis takes ~60px, padding ~20px each side)
          // Recharts typically uses ~60px for Y-axis and ~20px padding on each side
          const plotAreaWidth = width - 100;
          const plotX = Math.max(0, Math.min(plotAreaWidth, mouseX - 60));
          const timestamp = minTime + (plotX / plotAreaWidth) * timeRange;
          
          // Only update if within the domain range
          if (timestamp >= minTime && timestamp <= maxTime) {
            setActiveTimestamp(timestamp);
          }
        }}
        onMouseLeave={() => {
          // Clear activeTimestamp when mouse leaves the chart area
          setTimeout(() => {
            setActiveTimestamp(null);
            setMousePosition(null);
          }, 200);
        }}
      >
            <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={filteredData} 
            syncId="priceChart"
            margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
          >
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.2} />
                <XAxis
                  type="number"
                  dataKey="timeValue"
                  domain={xAxisDomain}
                  ticks={xAxisTicks}
                  stroke="#94a3b8"
                  fontSize={10}
                  tickFormatter={formatTimeTick}
                  allowDecimals={false}
                />
                <YAxis
                  stroke="#94a3b8"
                  fontSize={10}
                  domain={['dataMin - 1', 'dataMax + 1']}
                  tickFormatter={formatPrice}
                  allowDataOverflow={false}
                />
                 <Tooltip 
                   content={<CustomTooltip />} 
                   cursor={false}
                   active={activeTimestamp !== null}
                   labelFormatter={() => activeTimestamp}
                   allowEscapeViewBox={{ x: false, y: false }}
                 />
                {/* Synchronized reference line showing active timestamp */}
                {activeTimestamp !== null && (
                  <ReferenceLine
                    x={activeTimestamp}
                    stroke="#94a3b8"
                    strokeWidth={1.5}
                    strokeDasharray="2 2"
                    strokeOpacity={0.6}
                  />
                )}

            {/* Last Price Line - Solid, thin, orange */}
            {visibleTimeframes.lastPrice && (
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#f59e0b"
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={false}
                  name="price"
                  isAnimationActive={false}
                />
            )}

                {/* VWAP Line - Purple, solid, thin */}
            {visibleTimeframes.vwap && (
                  <Line
                    type="monotone"
                    dataKey="vwap"
                    stroke="#8b5cf6"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={false}
                    name="vwap"
                    isAnimationActive={false}
                  />
                )}

                {/* VWAMP Line - Pink, solid, thin */}
            {visibleTimeframes.vwamp && (
                  <Line
                    type="monotone"
                    dataKey="vwamp"
                    stroke="#ec4899"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={false}
                    name="vwamp"
                    isAnimationActive={false}
                  />
                )}

            {/* TWAP Lines - Solid, thin lines with different colors */}
            {visibleTimeframes.twap1m && (
                    <Line
                      type="monotone"
                dataKey="twap1m" 
                stroke="#ef4444" 
                strokeWidth={1.5}
                      dot={false}
                      activeDot={false}
                name="twap1m"
                isAnimationActive={false}
              />
            )}
            {visibleTimeframes.twap5m && (
                  <Line
                    type="monotone"
                    dataKey="twap5m"
                    stroke="#3b82f6"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={false}
                    name="twap5m"
                    isAnimationActive={false}
                  />
                )}
            {visibleTimeframes.twap15m && (
                  <Line
                    type="monotone"
                    dataKey="twap15m"
                    stroke="#10b981"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={false}
                    name="twap15m"
                    isAnimationActive={false}
                  />
                )}
            {visibleTimeframes.twap30m && (
              <Line 
                type="monotone" 
                dataKey="twap30m" 
                stroke="#f97316" 
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                name="twap30m"
                isAnimationActive={false}
              />
            )}
            {visibleTimeframes.twap1h && (
                  <Line
                    type="monotone"
                    dataKey="twap1h"
                    stroke="#06b6d4"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={false}
                    name="twap1h"
                    isAnimationActive={false}
                  />
                )}
            {visibleTimeframes.twap4h && (
              <Line 
                type="monotone" 
                dataKey="twap4h" 
                stroke="#a855f7" 
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                name="twap4h"
                isAnimationActive={false}
              />
            )}
              
              {/* Brush for zooming */}
              {filteredData.length > 10 && (
                <Brush
                  dataKey="timeValue"
                  height={30}
                  stroke="#3b82f6"
                  fill="#3b82f640"
                  onChange={handleBrushChange}
                  startIndex={0}
                  endIndex={filteredData.length - 1}
                />
              )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Imbalance Chart */}
          <div style={{ marginTop: 8 }}>
            <div style={{ 
              fontSize: 11, 
              color: '#94a3b8', 
              marginBottom: 4,
              paddingLeft: 4,
              fontWeight: 500
            }}>
              Weighted Depth Imbalance
            </div>
            <div 
              style={{ height: 120, position: 'relative' }}
              onMouseMove={(e) => {
                // Convert mouse X position to timestamp value (same logic as top chart)
                if (!xAxisDomain) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const width = rect.width;
                const [minTime, maxTime] = xAxisDomain;
                const timeRange = maxTime - minTime;
                // Account for chart padding (Y-axis takes ~60px, padding ~20px each side)
                const plotAreaWidth = width - 100;
                const plotX = Math.max(0, Math.min(plotAreaWidth, mouseX - 60));
                const timestamp = minTime + (plotX / plotAreaWidth) * timeRange;
                
                // Only update if within the domain range
                if (timestamp >= minTime && timestamp <= maxTime) {
                  setActiveTimestamp(timestamp);
                }
              }}
              onMouseLeave={() => {
                // Clear activeTimestamp when mouse leaves the chart area
                setTimeout(() => setActiveTimestamp(null), 200);
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={filteredImbalanceData} syncId="priceChart">
                  <defs>
                    <linearGradient id="imbalanceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ec4899" stopOpacity={0.3}/>
                      <stop offset="100%" stopColor="#ec4899" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.3} />
                  <XAxis
                    type="number"
                    dataKey="timeValue"
                    domain={xAxisDomain}
                    ticks={xAxisTicks}
                    stroke="#94a3b8"
                    fontSize={9}
                    tickFormatter={formatTimeTick}
                    allowDecimals={false}
                  />
                  <YAxis
                    domain={[0, 1]}
                    stroke="#94a3b8"
                    fontSize={9}
                    tickFormatter={(value) => value.toFixed(1)}
                    label={{ value: 'Imbalance', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#94a3b8', fontSize: 10 } }}
                  />
                  <Tooltip 
                    content={<ImbalanceTooltip />} 
                    cursor={false}
                    active={activeTimestamp !== null}
                    labelFormatter={() => activeTimestamp}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ maxHeight: '120px', top: 'auto', bottom: '10px' }}
                  />
                  {/* Synchronized reference line showing active timestamp */}
                  {activeTimestamp !== null && (
                    <ReferenceLine
                      x={activeTimestamp}
                      stroke="#ec4899"
                      strokeWidth={2}
                      strokeDasharray="0"
                      strokeOpacity={0.8}
                    />
                  )}
                  <ReferenceLine
                    y={0}
                    stroke="#f59e0b"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    strokeOpacity={0.6}
                    label={{ value: "0", position: "right", fill: "#f59e0b", fontSize: 10 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="imbalance"
                    stroke="#ec4899"
                    strokeWidth={2}
                    fill="url(#imbalanceGradient)"
                    fillOpacity={1}
                    dot={false}
                    activeDot={false}
                    name="imbalance"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{
        marginTop: 8, 
        fontSize: 11, 
        color: '#64748b',
        textAlign: 'center'
      }}>
        Last update: {data[data.length - 1]?.time}
          </div>
    </div>
  );
}